"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { LayoutDashboard, BookOpen, FileCheck2, Gift, Award, CreditCard, Compass, GraduationCap, ShoppingBag, Share2, User, CalendarDays, Link2, ExternalLink, Trophy, MessageCircle, PlayCircle } from "lucide-react";
import { ModernSidebar } from "@/components/ui/modern-side-bar";
import { DashboardAnnouncementBanner } from "@/components/dashboard/DashboardAnnouncementBanner";
import { Skeleton, StatCardSkeleton } from "@/components/ui/Skeleton";
import { GlobalSearch, GlobalSearchFab } from "@/components/search/GlobalSearch";
import HelpWidgetButton from "@/components/support/HelpWidgetButton";
import { authApi, invalidateCache } from "@/lib/api-client";
import { useMenuVisit } from "@/hooks/use-menu-visit";
import { useScrollReset } from "@/hooks/use-scroll-reset";
import { useTenant, useTenantFeatures, useTenantNavOverrides, useTerm, useMonetizationMode, hasMembershipTier, isFeatureVisible } from "@/components/tenant/TenantProvider";
export const userSections = [{
  id: "overview",
  label: "Ringkasan",
  items: [{
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    href: "/dashboard"
  }]
}, {
  id: "learning",
  label: "Belajar Saya",
  items: [{
    id: "my-classes",
    label: "Kelas Saya",
    icon: BookOpen,
    href: "/my-classes",
    courseOnly: true
  }, {
    id: "kelas",
    label: "Katalog Kelas",
    icon: GraduationCap,
    href: "/kelas",
    courseOnly: true
  }, {
    id: "products",
    label: "Produk Saya",
    icon: ShoppingBag,
    href: "/products"
  }, {
    id: "tryout",
    label: "Tryout",
    icon: FileCheck2,
    href: "/tryout",
    badge: "New",
    feature: "tryout",
    courseOnly: true
  }, {
    id: "rekaman-zoom",
    label: "Rekaman Zoom",
    icon: PlayCircle,
    href: "/rekaman-zoom",
    feature: "webinar",
    courseOnly: true
  }, {
    id: "bonuses",
    label: "Bonus",
    icon: Gift,
    href: "/bonuses"
  }]
}, {
  id: "community",
  label: "Komunitas",
  items: [{
    id: "community",
    label: "Komunitas",
    icon: MessageCircle,
    href: "/community",
    feature: "community"
  }]
}, {
  id: "explore",
  label: "Eksplorasi",
  items: [{
    id: "explore",
    label: "Jelajahi",
    icon: Compass,
    href: "/explore"
  }, {
    id: "webinars",
    label: "Webinar",
    icon: CalendarDays,
    href: "/webinars",
    feature: "webinar"
  }, {
    id: "certificates",
    label: "Sertifikat",
    icon: Award,
    href: "/certificates",
    courseOnly: true
  }, {
    id: "transactions",
    label: "Transaksi",
    icon: CreditCard,
    href: "/transactions"
  }]
}, {
  id: "business",
  label: "Affiliate & Bisnis",
  items: [{
    id: "affiliate",
    label: "Affiliate",
    icon: Share2,
    href: "/affiliate",
    feature: "affiliate"
  }, {
    id: "growth-ambassador",
    label: "Leaderboard",
    icon: Trophy,
    href: "/growth-ambassador",
    badge: "New",
    feature: "gamification"
  }, {
    id: "shortlinks",
    label: "Shortlinks",
    icon: Link2,
    href: "/shortlinks",
    feature: "affiliate"
  }]
}, {
  id: "account",
  label: "Akun",
  items: [{
    id: "account",
    label: "Akun Saya",
    icon: User,
    href: "/account"
  }]
}, {
  id: "help",
  label: "Bantuan",
  items: [{
    id: "panduan",
    label: "Panduan",
    icon: BookOpen,
    href: "/docs/",
    external: true
  }]
}];
function DashboardSkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-9 w-36 rounded-lg" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <aside className="hidden w-[var(--sidebar-w)] shrink-0 border-r border-[rgba(0,0,0,0.07)] bg-white p-5 md:block">{t0}<div className="mt-8 space-y-3">{Array.from({
          length: 8
        }).map(_temp)}</div></aside>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton className="h-9 w-56 rounded-lg" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="dashboard-shell flex h-dvh overflow-hidden">{t1}<main className="flex-1 p-4 pt-5 md:p-7 lg:p-9"><div className="mx-auto max-w-7xl space-y-6">{t2}<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{Array.from({
              length: 4
            }).map(_temp2)}</div><Skeleton className="h-80 rounded-lg" /></div></main></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function _temp2(__0, i_0) {
  return <StatCardSkeleton key={i_0} />;
}
function _temp(_, i) {
  return <Skeleton key={i} className="h-10 rounded-lg" />;
}
interface SidebarUser {
  name: string;
  initials: string;
  role?: string;
  roleColor?: string;
  avatarUrl?: string | null;
  avatarSeed?: string;
}
function isPaidMembership(membership: {
  expires_at?: string | null;
} | null | undefined): boolean {
  if (!membership) return false;
  if (!membership.expires_at) return true; // lifetime
  return new Date(membership.expires_at) > new Date();
}
export default function UserDashboardLayout(t0) {
  const $ = _c(44);
  const {
    children
  } = t0;
  const pathname = usePathname();
  useMenuVisit();
  const scrollRef = useRef(null);
  useScrollReset(scrollRef);
  const branding = useTenant();
  const features = useTenantFeatures();
  const allNavOverrides = useTenantNavOverrides();
  const navOverrides = allNavOverrides.user;
  const sectionOverrides = allNavOverrides.user_sections;
  const term = useTerm();
  const monetizationMode = useMonetizationMode();
  let t1;
  if ($[0] !== allNavOverrides.customLinks || $[1] !== features || $[2] !== monetizationMode || $[3] !== navOverrides || $[4] !== sectionOverrides) {
    let t2;
    if ($[6] !== sectionOverrides) {
      t2 = s => !sectionOverrides[s.id]?.hidden;
      $[6] = sectionOverrides;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== features || $[9] !== monetizationMode || $[10] !== navOverrides || $[11] !== sectionOverrides) {
      t3 = s_0 => ({
        ...s_0,
        label: sectionOverrides[s_0.id]?.label || s_0.label,
        items: s_0.items.filter(i => isFeatureVisible(features, (i as {
          feature?: string;
        }).feature)).filter(i_0 => !navOverrides[i_0.id]?.hidden).filter(i_1 => !((i_1 as {
          courseOnly?: boolean;
        }).courseOnly && monetizationMode === "digital_product")).map(i_2 => navOverrides[i_2.id]?.label ? {
          ...i_2,
          label: navOverrides[i_2.id].label
        } : i_2)
      });
      $[8] = features;
      $[9] = monetizationMode;
      $[10] = navOverrides;
      $[11] = sectionOverrides;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    const visibleSections = userSections.filter(t2).map(t3).filter(_temp3);
    let t4;
    if ($[13] !== allNavOverrides.customLinks) {
      const customLinks = allNavOverrides.customLinks ?? [];
      t4 = customLinks.filter(_temp4).map(_temp5);
      $[13] = allNavOverrides.customLinks;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    const customLinkItems = t4;
    t1 = customLinkItems.length > 0 ? [...visibleSections, {
      label: "Tautan",
      items: customLinkItems
    }] : visibleSections;
    $[0] = allNavOverrides.customLinks;
    $[1] = features;
    $[2] = monetizationMode;
    $[3] = navOverrides;
    $[4] = sectionOverrides;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const sidebarSections = t1;
  const redirectPathRef = useRef(pathname);
  const authStartedRef = useRef(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  let t2;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "Member",
      initials: "M"
    };
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const [sidebarUser, setSidebarUser] = useState(t2);
  let t3;
  if ($[16] !== isPaid || $[17] !== monetizationMode || $[18] !== term) {
    t3 = monetizationMode === "digital_product" ? term("member", "Pelanggan") : monetizationMode === "per_class" ? term("member", "Siswa") : `${term("member", "Member")} ${isPaid ? term("premium", "Premium") : "Gratis"}`;
    $[16] = isPaid;
    $[17] = monetizationMode;
    $[18] = term;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const t4 = hasMembershipTier(monetizationMode) && isPaid ? "text-amber-500 font-semibold" : "text-gray-400";
  let t5;
  if ($[20] !== sidebarUser || $[21] !== t3 || $[22] !== t4) {
    t5 = {
      ...sidebarUser,
      role: t3,
      roleColor: t4
    };
    $[20] = sidebarUser;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const sidebarUserResolved = t5;
  let t6;
  let t7;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (authStartedRef.current) {
        return;
      }
      authStartedRef.current = true;
      authApi.me().then(res => {
        if (!res.success) {
          window.location.href = `/login?redirect=${encodeURIComponent(redirectPathRef.current)}`;
        } else {
          const data = res.data as {
            user?: {
              id?: string;
              name?: string;
              email?: string;
              avatar_url?: string | null;
            };
            membership?: {
              expires_at?: string | null;
            } | null;
          } | undefined;
          const user = data?.user;
          const membership = data?.membership;
          const paid = isPaidMembership(membership);
          const name = user?.name || "Member";
          const initials = name.split(" ").map(_temp6).join("").slice(0, 2).toUpperCase();
          setIsPaid(paid);
          setSidebarUser({
            name,
            initials,
            avatarUrl: user?.avatar_url || null,
            avatarSeed: user?.id || user?.email || name
          });
          setAuthChecked(true);
        }
      });
    };
    t7 = [];
    $[24] = t6;
    $[25] = t7;
  } else {
    t6 = $[24];
    t7 = $[25];
  }
  useEffect(t6, t7);
  const handleLogout = _temp7;
  if (!authChecked) {
    let t8;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <DashboardSkeleton />;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    return t8;
  }
  const t8 = branding.landing?.tagline || "";
  let t9;
  if ($[27] !== branding.brand_name || $[28] !== t8) {
    t9 = {
      name: branding.brand_name,
      subtitle: t8
    };
    $[27] = branding.brand_name;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = ["Ringkasan", "Belajar Saya", "Komunitas", "Eksplorasi"];
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== sidebarSections || $[32] !== sidebarUserResolved || $[33] !== t9) {
    t11 = <ModernSidebar brand={t9} sections={sidebarSections} user={sidebarUserResolved} onLogout={handleLogout} collapsibleSections={true} defaultOpenSections={t10} />;
    $[31] = sidebarSections;
    $[32] = sidebarUserResolved;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== children || $[36] !== pathname) {
    t12 = <div ref={scrollRef} className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden"><main id="main-content" tabIndex={-1} className="flex-1 p-4 pt-5 pb-[calc(var(--nav-h)+1rem)] outline-none md:p-7 lg:p-9"><div key={pathname} className="page-enter mx-auto max-w-7xl md:pb-10">{children}</div></main></div>;
    $[35] = children;
    $[36] = pathname;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  let t14;
  let t15;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <GlobalSearch />;
    t14 = <GlobalSearchFab />;
    t15 = <HelpWidgetButton />;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t13 = $[38];
    t14 = $[39];
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t11 || $[42] !== t12) {
    t16 = <div className="dashboard-shell flex h-dvh overflow-hidden">{t11}{t12}{t13}{t14}{t15}</div>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
}
async function _temp7() {
  await authApi.logout();
  invalidateCache();
  window.location.href = "/login";
}
function _temp6(w) {
  return w[0];
}
function _temp5(l_0) {
  return {
    id: l_0.id,
    label: l_0.label,
    icon: l_0.type === "external" ? ExternalLink : Link2,
    href: l_0.target,
    external: l_0.type === "external"
  };
}
function _temp4(l) {
  return !l.hidden;
}
function _temp3(s_1) {
  return s_1.items.length > 0;
}